import { PATHS } from './../../shared/constants/paths';
import { Component, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState } from '@panamax/app-state';
import { PageTitle } from 'src/app/shared/constants/page-titles.enum';
import { BrowseCategoryService } from '../services/browse-category.service';
import { SearchFilterEnum } from '../shared/models/search-filter.enum';
import { environment } from 'src/environments/environment';
import { CategoryItem } from '../models/category.model';
import { Message } from 'src/app/ngrx-message/models/client/message';
import { MessageTypeEnum } from 'src/app/ngrx-message/constants/messageTypeEnum';
import { MessageStoreService } from 'src/app/ngrx-message/services/message/message-store.service';
import { SearchAnalyticsService } from '../services/search-analytics.service';
import { Subscription } from 'rxjs';
import { IonContent, ViewDidLeave } from '@ionic/angular';
import { BrowseCategoryState } from '@usf/ngrx-product';
import { filter } from 'rxjs/operators';
import { PlatformService } from '@panamax/app-state';
import { NavigationHelperService } from 'src/app/shared/helpers/navigation.helpers.service';
import { SEARCH_PAGES_ENUM } from '../../shared/constants/search-pages.enum';
import { EndecaSearchPagesTypes } from '../../shared/constants/endeca-search-pages-types';
import { SearchService } from '../services/search.service';

@Component({
  selector: 'app-catalog',
  templateUrl: './catalog.page.html',
  styleUrls: ['./catalog.page.scss'],
})
export class CatalogPage implements ViewDidLeave {
  @ViewChild(IonContent) content: IonContent;
  searchType = SearchFilterEnum;
  classCategoryUri = `${environment.mediaServerUrl}/`;
  errorMessage = 'We’re sorry, that page couldn’t be loaded. Please try again.';
  catalogSubscription$: Subscription;
  backgroundImageStyle = "url('/assets/images/browse-hero.webp')";
  overlayGradient =
    'linear-gradient(90deg, rgba(47,47,47,0.77) 0%, rgba(47,47,47,0.77) 8%, rgba(47,47,47,0) 50%, rgba(47,47,47,0.77) 91%, rgba(47,47,47,0.77) 100%)';
  showMoreOptions = false;
  categoryList: CategoryItem[] = [];
  subcategoryList: CategoryItem[] = [];
  subCategoryId = '0';
  whileSuppliesLastUrlImg =
    'https://www.usfoods.com/content/dam/usf/images/category/OTH-20220913-2066973%20MOXe%20Category%20Image-While%20Supplies%20Last.jpg';

  catalogConfig = {
    messages: {
      category: {
        default: this.translateService.instant(
          'i18n.search.searchCatalogPage.productCategories',
        ),
        value: this.translateService.instant(
          'i18n.search.searchCatalogPage.productCategories',
        ),
      },
      viewAll: {
        default: this.translateService.instant(
          'i18n.search.searchCatalogPage.viewAllProducts',
        ),
        value: this.translateService.instant(
          'i18n.search.searchCatalogPage.viewAllProducts',
        ),
      },
    },
    category: {
      maxLength: 9,
      viewLessButton: false,
    },
    subcategory: {
      maxLength: 9,
      hasMoreItems: false,
      view: false,
    },
    selectedCategory: { dimensionId: 0, pageType: EndecaSearchPagesTypes.all },
    loading: false,
  };

  constructor(
    protected panAppState: PanAppState,
    private translateService: TranslateService,
    private browseService: BrowseCategoryService,
    private messageStoreService: MessageStoreService,
    private searchAnalyticsService: SearchAnalyticsService,
    readonly platformService: PlatformService,
    private navigationHelper: NavigationHelperService,
    private readonly searchService: SearchService,
  ) {}

  ionViewWillEnter() {
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.browseProducts),
    );
    this.browseService.executeBrowse({});
    this.catalogSubscription$ = this.browseService
      .getCategories(this.subCategoryId)
      .subscribe(category => this.loadCategory(category));
  }

  loadCategory(category: BrowseCategoryState) {
    if (category?.loadingState === 'error') {
      this.catalogConfig = { ...this.catalogConfig, loading: false };
      this.showWarningToastMessage();
      this.returnToCategoryView();
      return;
    }
    if (category?.loadingState === 'loading') {
      this.catalogConfig = { ...this.catalogConfig, loading: true };
      return;
    }
    this.catalogConfig = { ...this.catalogConfig, loading: false };
    if (
      this.catalogConfig.subcategory.view &&
      this.subCategoryId &&
      category?.loadingState === 'loaded'
    ) {
      this.subcategoryList = category.category as CategoryItem[];
      this.catalogConfig.subcategory.hasMoreItems =
        this.subcategoryList.length > 9;
      return;
    }
    //main categories has by by default O as selectorId
    if (this.categoryList.length === 0 && category?.loadingState === 'loaded') {
      this.categoryList = category.category as CategoryItem[];
      this.catalogConfig.subcategory.view = false;
      this.catalogConfig.selectedCategory = {
        dimensionId: 0,
        pageType: EndecaSearchPagesTypes.all,
      };
    }
  }
  ionViewDidEnter() {
    this.searchAnalyticsService.trackCatalogPageLoad();
  }
  ionViewDidLeave() {
    if (this.catalogSubscription$) {
      this.catalogSubscription$.unsubscribe();
    }
  }
  viewAllCategories(): void {
    this.showMoreOptions = !this.showMoreOptions;
    this.catalogConfig.category.maxLength = this.categoryList.length;
    this.catalogConfig.category.viewLessButton = true;
  }
  viewLessCategories(): void {
    this.showMoreOptions = !this.showMoreOptions;
    this.catalogConfig.category.viewLessButton = false;
    this.catalogConfig.category.maxLength = 9;
  }
  viewAllSubCategories(): void {
    this.catalogConfig.subcategory.hasMoreItems = false;
    this.catalogConfig.subcategory.maxLength = this.subcategoryList.length;
  }
  viewSubcategory({ dimensionId, description }): void {
    this.catalogConfig.subcategory.view = true;
    this.catalogConfig.selectedCategory.dimensionId = dimensionId;
    this.subCategoryId = '' + dimensionId;
    this.setCategoryMessages(description, `View All ${description}`);
    this.browseService.executeBrowse({
      searchFilterProperties: `${dimensionId}`,
    });
    this.catalogSubscription$?.unsubscribe();
    this.catalogSubscription$ = this.browseService
      .getCategories(this.subCategoryId)
      .pipe(filter(cat => cat != null))
      .subscribe(category => {
        this.loadCategory(category);
        this.content.scrollToTop();
      });

    this.browseService.trackCatalogCategoryPageLoad(description);
  }
  viewCategoryProducts({ dimensionId, pageType }): void {
    const queryParams = {
      searchFilterProperties: `${dimensionId}`,
      categoryId: `${dimensionId}`,
      pageType: pageType || EndecaSearchPagesTypes.catalog,
      originSearchPage: SEARCH_PAGES_ENUM.catalog,
    };
    this.navigationHelper.routeTo(PATHS.SEARCH, queryParams);
  }
  returnToCategoryView(): void {
    this.subCategoryId = '0';
    this.subcategoryList = [];
    // reset config
    this.catalogConfig.subcategory.view = false;
    this.catalogConfig.subcategory.hasMoreItems = false;
    this.catalogConfig.subcategory.maxLength = 9;
    this.setCategoryMessages(
      this.catalogConfig.messages.category.default,
      this.catalogConfig.messages.viewAll.default,
    );
  }
  setCategoryMessages(category: string, viewAll: string): void {
    this.catalogConfig.messages.category.value = category;
    this.catalogConfig.messages.viewAll.value = viewAll;
  }
  showWarningToastMessage() {
    this.messageStoreService.upsertMessage({
      id: `id-${this.errorMessage}`,
      stack: null,
      watermark: new Date().toUTCString(),
      read: false,
      type: MessageTypeEnum.warning,
      display: this.errorMessage,
      toast: true,
    } as Message);
  }

  viewAllProducts() {
    this.viewCategoryProducts({ dimensionId: 0, pageType: 'all' });
  }

  goToWhileSuppliesLast() {
    this.searchService.goToWhileSuppliesLast();
  }
}
